import { useQuery } from '@tanstack/react-query';
import { useContext, useMemo, useState } from 'react';
import { UNSAFE_LocationContext } from 'react-router';

import { AppointmentUpdatesPrompt } from '@/features/storefront/pwa/OrderUpdatesPrompt';
import { useStorefrontData, useStorefrontScope } from '@/features/storefront/data/context';
import { StorefrontImage } from '@/features/storefront/components/StorefrontImage';
import { CheckoutPausedNote } from '@/features/storefront/components/CheckoutPause';
import { useCheckoutPause } from '@/features/storefront/hooks/useCheckoutPause';
import { useShopContent } from '@/features/storefront/content/useContent';
import { useStorefrontLanguage } from '@/features/storefront/i18n/context';
import { NO_TEAM, type StorefrontTeam } from '@/features/storefront/api/team';
import {
  DAYS_SHOWN,
  calendarLocale,
  firstOpenDay,
  firstOpenPage,
  formatDay,
  formatTime,
  initialPerson,
  stripDays,
} from '@/features/storefront/sections/bookingDays';
import type { TokenSession } from '@/features/storefront/api/storefront';
import {
  bookingPhoneOk,
  DayStrip,
  Field,
  QuestionField,
  TokenSessionCards,
  WhatsAppOptIn,
} from '@/features/storefront/sections/bookingParts';
import { useBookingWhatsApp } from '@/features/storefront/hooks/useStorefrontWhatsApp';
import {
  BookingPaymentOptions,
  BookingPaymentOutcome,
} from '@/features/storefront/sections/bookingPayment';
import { useBookingPayment } from '@/features/storefront/sections/useBookingPayment';
import { openSlots, sessionKey, staffSlots } from '@/features/storefront/sections/bookingSlots';
import {
  answersPayload,
  firstUnanswered,
  shownQuestions,
  type BookingAnswers,
} from '@/features/storefront/sections/bookingSteps';
import type {
  BookingHours,
  BookingService,
  BookingsSection,
} from '@/features/storefront/sections/schema';
import {
  bookableStaff,
  combinedHours,
  memberHours,
  memberPhoto,
} from '@/features/storefront/sections/team';
import { STOREFRONT_BUTTON_STYLE } from '@/features/storefront/theme';
import { cn } from '@/lib/cn';
import { fixedImageSizes } from '@/lib/imageRenditions';

/**
 * Pick a day, pick a time, leave your details.
 *
 * The half of the bookings section that is a widget rather than a layout, kept
 * out of `SectionRenderer` for that reason: a date strip, a live slot grid and
 * a form with three states between them are not the shape of the other section
 * renderers, and inlining them would bury the ones that are.
 *
 * **The shop owns the calendar.** Nothing here works out whether a time is
 * free. The grid is asked of the storefront's own data source, per service and
 * per day, and asked again after a booking - because a slot list a browser
 * reasoned its way to is a list of times it merely hoped were free, and the
 * shopper finds out at the moment that matters most. On a merchant's shop that
 * source is the API; in a theme's sample shop it is the written-down one, which
 * keeps its own diary for the life of the tab. The one caller that asks nobody
 * is the customiser, which has no saved section for either of them to know
 * about; see `preview` below and `sections/bookingSlots.ts`.
 *
 * Times are the *shop's*. The availability response names the store's zone and
 * every time printed here is formatted in it, so a clinic in Kolkata offers its
 * ten o'clock to someone reading in London as 10:00 rather than 05:30.
 *
 * This is the one-page form (`form_layout: "single"`), drawn exactly as it
 * always was; the merchant's own questions join it after the notes, and
 * `date_picker: "strip"` swaps its row of days for the fuller carousel. The
 * step-by-step shape of the same booking is `BookingWizard`, built from the
 * same parts (`bookingDays.ts`, `bookingParts.tsx`).
 */
export interface BookingCalendarProps {
  section: BookingsSection;
  /** The service the shopper chose. Its length decides the grid. */
  service: BookingService;
  /** True in the customiser: nothing is fetched and nothing is booked. */
  preview: boolean;
  /**
   * The shop's visible people, on a section that books by person (`null`
   * while they load). Ignored when `staff_mode` is none, which is what keeps
   * that mode exactly the calendar it was before people existed.
   */
  team?: StorefrontTeam | null;
  /**
   * The slug of the person to start a choose-mode calendar on, in place of
   * the address's `?member=` - what a person's own profile page passes when
   * it embeds their calendar, where the address names nobody.
   */
  member?: string | null;
  /**
   * The narrow card (`BookingPanel`) beside a profile or a service: a third
   * of the page at most, so the slot grid stays three across, the form is one
   * field per row and the button runs the card's width. The calendar itself -
   * what is asked of the shop, what is booked - is the same either way.
   */
  compact?: boolean;
}

export function BookingCalendar({
  section,
  service,
  preview,
  team: given,
  member: named = null,
  compact = false,
}: BookingCalendarProps) {
  const copy = useShopContent();
  // Read-only maintenance refuses a booking; a preview is never sent anyway.
  const pausedNow = useCheckoutPause();
  const paused = preview ? null : pausedNow;
  const { locale } = useStorefrontLanguage();
  const scope = useStorefrontScope();
  const source = useStorefrontData();

  // --- Who the appointment is with ----------------------------------------
  // Everything in this block is inert when the section does not book by
  // person: nothing is fetched, `people` is empty, `person` is null, and the
  // week is the section's own - the calendar this component always drew.
  const byPerson = section.staff_mode !== 'none';
  // The router's address where there is a router (the shop, its preview);
  // the customiser has none, and `memberFromSearch` falls back to the window.
  const routed = useContext(UNSAFE_LocationContext) as { location: { search: string } } | null;
  // The page's list when it brought one; otherwise asked for here, under the
  // same key, so the two never fetch the shop's people twice.
  const own = useQuery({
    queryKey: [...scope, 'team-members'],
    queryFn: async () => (source.team ? await source.team() : NO_TEAM),
    enabled: byPerson && given === undefined,
    retry: false,
  });
  const team: StorefrontTeam | null =
    given !== undefined ? given : own.isError ? NO_TEAM : (own.data ?? null);
  const people = useMemo(
    () => bookableStaff(section, service, team?.members ?? []),
    [section, service, team],
  );
  // `undefined` until the shopper picks, so the starting person can follow
  // the team as it arrives (a `?member=` link names someone who is only known
  // once the list has loaded).
  const [picked, setPicked] = useState<string | null | undefined>(undefined);
  const person: string | null =
    section.staff_mode === 'fixed'
      ? section.staff_member_id
      : section.staff_mode === 'choose'
        ? picked !== undefined
          ? picked
          : initialPerson(section, people, routed?.location.search ?? null, named)
        : null;
  const personRow = person ? (people.find((member) => member.id === person) ?? null) : null;
  // Whose week the strip shows: the person's, everyone's for "any available",
  // or the section's.
  const week = useMemo<BookingHours[]>(() => {
    if (!byPerson) return section.hours;
    if (personRow) return memberHours(section, personRow);
    if (people.length > 0) return combinedHours(people.map((member) => memberHours(section, member)));
    return section.hours;
  }, [byPerson, section, personRow, people]);

  // `undefined` page and day mean "where this week first opens", so changing
  // person moves the strip to the days they actually work.
  const [pageChoice, setPage] = useState<number | undefined>(undefined);
  const page = pageChoice ?? firstOpenPage(section, week);
  const days = useMemo(() => stripDays(section, page, week), [section, page, week]);

  // The first day the shop is actually open, rather than today: a salon shut on
  // Sunday should not greet a Sunday visitor with an empty grid.
  const [dayChoice, setDay] = useState<string | undefined>(undefined);
  const day = dayChoice ?? firstOpenDay(section, week);
  const [chosen, setChosen] = useState<string | null>(null);
  // A token instead of a time, for a person who sees patients in order of
  // token: the session picked. One or the other of `chosen` and this.
  const [token, setToken] = useState<TokenSession | null>(null);
  const [values, setValues] = useState({ name: '', email: '', phone: '', notes: '', address: '' });
  // "Updates on WhatsApp": offered only where the shop sends them and the
  // section asks for the number they would go to, and only ever sent with one.
  const offersWhatsApp = useBookingWhatsApp(!preview);
  const [whatsappOptIn, setWhatsAppOptIn] = useState(false);
  // The merchant's own questions, by id. None is the form it always was.
  const questions = shownQuestions(section);
  const [answers, setAnswers] = useState<BookingAnswers>({});
  const [state, setState] = useState<'idle' | 'sending' | 'done' | 'error'>('idle');
  const [error, setError] = useState('');
  const [booked, setBooked] = useState<{
    start: string;
    zone?: string;
    with: string | null;
    /** The patient's number, when a token was taken; `start` is then an estimate. */
    token: number | null;
    /** For following it with push notifications. */
    id: string;
    accessToken: string | null;
  } | null>(null);

  // On the live shop a choose-mode section waits for its people, and says so
  // when nobody offers this service rather than drawing a diary nobody keeps.
  // The customiser draws the plain grid instead: a merchant who has not added
  // anyone yet still has to see the calendar they are designing.
  const waitingForPeople = section.staff_mode === 'choose' && !preview && team === null;
  const nobody =
    section.staff_mode === 'choose' && !preview && team !== null && people.length === 0;

  const teamMemberId = byPerson ? person : undefined;
  const availability = useQuery({
    queryKey: [
      ...scope,
      'booking-availability',
      section.id,
      service.id,
      day,
      // Only on a section that books by person, so the key of one that does
      // not is the key it always was.
      ...(byPerson ? [person ?? 'any'] : []),
    ],
    queryFn: () => source.bookingAvailability({ section, service, day, teamMemberId }),
    enabled: !preview && day.length > 0 && !waitingForPeople && !nobody,
    // Availability is the one thing on a storefront that must never be served
    // from cache: a slot list a minute old is a double booking.
    staleTime: 0,
    gcTime: 0,
    retry: false,
  });

  const zone = availability.data?.timezone;
  const previewSlots =
    byPerson && people.length > 0
      ? staffSlots(section, service, day, personRow ? [personRow] : people)
      : openSlots(section, service, day);
  const slots = preview ? previewSlots : (availability.data?.slots ?? []);
  const bookable = slots.filter((slot) => slot.available);
  // Token sessions: in place of the grid for a person who books by token
  // (`mode` tokens), or under everyone else's times for "any available".
  const sessions = preview ? [] : (availability.data?.sessions ?? []);
  const tokenMode = !preview && availability.data?.mode === 'tokens';
  const needsAddress = service.location === 'address';
  // What booking costs now and the payment once booked; nothing on a shop
  // without booking payments.
  const payment = useBookingPayment(service, preview);

  const set = (field: keyof typeof values) => (event: { target: { value: string } }) =>
    setValues((current) => ({ ...current, [field]: event.target.value }));

  function chooseDay(next: string) {
    setDay(next);
    // The chosen time belonged to the old day. Keeping it would let a shopper
    // submit Tuesday's ten o'clock from Wednesday's grid.
    setChosen(null);
    setToken(null);
    setState('idle');
  }

  function choosePerson(next: string | null) {
    setPicked(next);
    // Another person is another diary: their days, their times. The strip
    // goes back to where *their* week opens and the time picked from the old
    // diary is dropped.
    setPage(undefined);
    setDay(undefined);
    setChosen(null);
    setToken(null);
    setState('idle');
  }

  function chooseSlot(startAt: string) {
    setChosen(startAt);
    setToken(null);
  }

  function chooseSession(session: TokenSession) {
    setToken(session);
    setChosen(null);
  }

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    if (preview || paused || (!chosen && !token)) return;
    // Asked here as well as by the field's `required`, because a form can be
    // sent without the browser's own check (and the server refuses it anyway).
    if (!bookingPhoneOk(values.phone)) {
      setError(copy('booking.phone_required'));
      setState('error');
      return;
    }
    if (needsAddress && !values.address.trim()) {
      setError(copy('booking.address_required'));
      setState('error');
      return;
    }
    // The merchant's required questions, for the same reason: the browser's
    // own check is skippable, and the server would refuse it anyway.
    const unanswered = firstUnanswered(questions, answers);
    if (unanswered) {
      setError(copy('booking.answer_required', { question: unanswered.label.trim() }));
      setState('error');
      return;
    }

    setState('sending');
    setError('');
    try {
      const confirmation = await source.createBooking({
        section,
        service,
        // A token is the session and whose it is - the server gives the number.
        ...(token
          ? { sessionStartsAt: token.starts_at, teamMemberId: token.team_member_id }
          : { startAt: chosen ?? undefined, ...(byPerson ? { teamMemberId: person } : {}) }),
        name: values.name.trim(),
        // A name and a phone number are all a booking needs.
        email: values.email.trim() || undefined,
        phone: values.phone.trim(),
        // Sent only where the merchant asked for them, so a field they turned
        // off cannot arrive full of whatever was typed before they did.
        notes: section.ask_notes ? values.notes.trim() || undefined : undefined,
        ...(needsAddress ? { customerAddress: values.address.trim() } : {}),
        answers: answersPayload(questions, answers),
        ...(offersWhatsApp && whatsappOptIn && values.phone.trim() ? { whatsappOptIn: true } : {}),
        ...payment.request(),
      });
      setBooked({
        start: confirmation.starts_at,
        zone,
        with: confirmation.team_member_name ?? personRow?.name ?? token?.team_member_name ?? null,
        token: confirmation.token_number ?? null,
        id: confirmation.id,
        accessToken: confirmation.access_token ?? null,
      });
      setState('done');
      setChosen(null);
      setToken(null);
      // A booking with money due keeps what was typed: if its hold runs out,
      // "pick another time" should not mean typing it all again.
      if (!confirmation.payment?.required) {
        setValues({ name: '', email: '', phone: '', notes: '', address: '' });
        setWhatsAppOptIn(false);
        setAnswers({});
      }
      void payment.settle(
        confirmation,
        { name: values.name.trim(), email: values.email.trim(), phone: values.phone.trim() },
        `${service.name} · ${copy('booking.when', {
          day: formatDay(confirmation.starts_at, locale, zone),
          time: formatTime(confirmation.starts_at, locale, zone),
        })}`,
      );
    } catch (caught) {
      // The message is the server's, because it is the one that knows what
      // went wrong: "that time has just been taken" is worth far more to a
      // shopper than a generic failure, and it tells them what to do next.
      setError(caught instanceof Error ? caught.message : '');
      setState('error');
      // The grid they were looking at is now known to be stale.
      void availability.refetch();
    }
  }

  const sessionCards = (showPerson: boolean) => (
    <TokenSessionCards
      sessions={sessions}
      chosen={token ? sessionKey(token) : null}
      onChoose={chooseSession}
      locale={locale}
      zone={zone}
      showPerson={showPerson}
      compact={compact}
    />
  );

  if (state === 'done' && booked) {
    return (
      <div
        className={cn('border p-6 text-center', compact ? 'mt-4' : 'mt-8')}
        style={{
          borderColor: 'var(--storefront-surface)',
          borderRadius: 'var(--storefront-radius)',
        }}
      >
        <p className="text-base font-semibold">{service.name}</p>
        {booked.token !== null ? (
          <p
            className="mt-3 text-3xl font-bold tabular-nums"
            style={{ color: 'var(--storefront-primary)' }}
            data-booking-token
          >
            {copy('booking.your_token', { number: booked.token })}
          </p>
        ) : null}
        <p className="mt-1 text-sm" style={{ color: 'var(--storefront-muted)' }}>
          {copy('booking.when', {
            day: formatDay(booked.start, locale, booked.zone),
            // A token's time is only an estimate, and says so.
            time:
              booked.token !== null
                ? copy('booking.approx', { time: formatTime(booked.start, locale, booked.zone) })
                : formatTime(booked.start, locale, booked.zone),
          })}
        </p>
        {booked.with ? (
          <p className="mt-1 text-sm" data-booking-with>
            {copy('booking.with_person', { name: booked.with })}
          </p>
        ) : null}
        {service.location !== 'place' ? (
          <p className="mt-1 text-sm" style={{ color: 'var(--storefront-muted)' }}>
            {copy(service.location === 'online' ? 'booking.location_online' : 'booking.location_address')}
          </p>
        ) : null}
        <BookingPaymentOutcome
          payment={payment}
          zone={booked.zone}
          onPickAnother={() => {
            payment.reset();
            setBooked(null);
            setState('idle');
            void availability.refetch();
          }}
        />
        {payment.phase.kind === 'expired' ? null : (
          <p className="mt-4 text-sm">{section.success_message}</p>
        )}
        {/* Reminders on this device, where the shop offers push. */}
        {payment.phase.kind === 'expired' ? null : (
          <AppointmentUpdatesPrompt appointmentId={booked.id} appointmentToken={booked.accessToken} />
        )}
      </div>
    );
  }

  // Where it happens, said once above the calendar. A service at the shop with
  // no note says nothing at all - which is every service written before this
  // existed, so those sections look exactly as they did.
  const where =
    service.location === 'online'
      ? copy('booking.location_online')
      : service.location === 'address'
        ? copy('booking.location_address')
        : '';
  const locationLine =
    where || service.location_note.trim() ? (
      <p className="mb-6 text-sm" data-booking-location={service.location}>
        {where ? <span className="font-semibold">{where}</span> : null}
        {where && service.location_note.trim() ? ' · ' : null}
        {service.location_note.trim() ? (
          <span style={{ color: 'var(--storefront-muted)' }}>{service.location_note.trim()}</span>
        ) : null}
      </p>
    ) : null;

  const singular = (team?.singular || 'Team member').toLocaleLowerCase(locale);
  const picker =
    section.staff_mode === 'choose' && people.length > 0 ? (
      <fieldset className="mb-6" data-staff-picker>
        <legend className="text-sm font-semibold">
          {section.staff_label ?? copy('booking.choose_person', { singular })}
        </legend>
        <div className="mt-3 flex flex-wrap gap-2">
          {[
            ...(section.allow_any_staff ? [null] : []),
            ...people.map((member) => member.id),
          ].map((id) => {
            const member = id ? people.find((entry) => entry.id === id) : null;
            const photo = member ? memberPhoto(member, team?.members) : null;
            const pressed = person === id;
            return (
              <button
                key={id ?? '__any'}
                type="button"
                aria-pressed={pressed}
                onClick={() => choosePerson(id)}
                className={cn(
                  'flex items-center gap-2 border px-3 py-2 text-start text-sm',
                  pressed && 'font-semibold',
                )}
                style={{
                  borderColor: pressed ? 'var(--storefront-primary)' : 'var(--storefront-surface)',
                  borderRadius: 'var(--storefront-radius)',
                }}
              >
                {photo ? (
                  <StorefrontImage
                    url={photo}
                    sizes={fixedImageSizes(32)}
                    alt=""
                    className="size-8 shrink-0 rounded-full object-cover"
                  />
                ) : null}
                <span>
                  <span className="block">{member ? member.name : copy('booking.any_person')}</span>
                  {member?.role ? (
                    <span
                      className="block text-xs font-normal"
                      style={{ color: 'var(--storefront-muted)' }}
                    >
                      {member.role}
                    </span>
                  ) : null}
                </span>
              </button>
            );
          })}
        </div>
      </fieldset>
    ) : null;

  // The one person a fixed section books with, named so the shopper knows.
  const fixedLine =
    section.staff_mode === 'fixed' && personRow ? (
      <p className="mb-6 text-sm" data-booking-fixed>
        {copy('booking.with_person', { name: personRow.name })}
      </p>
    ) : null;

  if (waitingForPeople || nobody) {
    return (
      <div className={compact ? 'mt-4' : 'mt-8'}>
        {locationLine}
        <p className="text-sm" style={{ color: 'var(--storefront-muted)' }} role="status">
          {copy(nobody ? 'booking.no_people' : 'booking.checking')}
        </p>
      </div>
    );
  }

  return (
    <div className={compact ? 'mt-4' : 'mt-8'} data-booking-compact={compact || undefined}>
      {locationLine}
      {picker}
      {fixedLine}
      {section.date_picker === 'strip' ? (
        <DayStrip
          section={section}
          days={days}
          day={day}
          page={page}
          locale={locale}
          onChoose={chooseDay}
          onPage={setPage}
        />
      ) : (
        <fieldset>
          <legend className="text-sm font-semibold">{copy('booking.choose_day')}</legend>
          <div className="mt-3 flex gap-2 overflow-x-auto pb-1">
            {days.map((entry) => (
              <button
                key={entry.iso}
                type="button"
                disabled={!entry.open}
                aria-pressed={entry.iso === day}
                onClick={() => chooseDay(entry.iso)}
                className={cn(
                  'shrink-0 border px-4 py-2 text-center text-sm disabled:opacity-40',
                  entry.iso === day && 'font-semibold',
                )}
                style={{
                  borderColor:
                    entry.iso === day ? 'var(--storefront-primary)' : 'var(--storefront-surface)',
                  borderRadius: 'var(--storefront-radius)',
                }}
              >
                <span className="block text-xs" style={{ color: 'var(--storefront-muted)' }}>
                  {new Intl.DateTimeFormat(calendarLocale(locale), { weekday: 'short' }).format(
                    entry.date,
                  )}
                </span>
                <span className="block">{entry.date.getDate()}</span>
              </button>
            ))}
          </div>
          {/* The rest of the merchant's window, a fortnight at a time. Absent
              entirely when the diary is not open past the first page, so a shop
              taking bookings a week ahead shows no controls to explore a month
              it does not have. */}
          {section.booking_window_days + 1 > DAYS_SHOWN ? (
            <div className="mt-2 flex gap-2">
              <button
                type="button"
                disabled={page === 0}
                onClick={() => setPage(Math.max(0, page - DAYS_SHOWN))}
                className="border px-3 py-1 text-xs disabled:opacity-40"
                style={{
                  borderColor: 'var(--storefront-surface)',
                  borderRadius: 'var(--storefront-radius)',
                }}
              >
                {copy('booking.earlier')}
              </button>
              <button
                type="button"
                disabled={page + DAYS_SHOWN > section.booking_window_days}
                onClick={() => setPage(page + DAYS_SHOWN)}
                className="border px-3 py-1 text-xs disabled:opacity-40"
                style={{
                  borderColor: 'var(--storefront-surface)',
                  borderRadius: 'var(--storefront-radius)',
                }}
              >
                {copy('booking.later')}
              </button>
            </div>
          ) : null}
        </fieldset>
      )}

      <fieldset className="mt-6">
        <legend className="text-sm font-semibold">{copy('booking.choose_time')}</legend>
        {availability.isPending && !preview ? (
          <p className="mt-3 text-sm" style={{ color: 'var(--storefront-muted)' }}>
            {copy('booking.checking')}
          </p>
        ) : availability.isError ? (
          <p className="mt-3 text-sm" style={{ color: 'var(--storefront-muted)' }}>
            {copy('booking.load_error')}
          </p>
        ) : tokenMode ? (
          sessions.length === 0 ? (
            <p className="mt-3 text-sm" style={{ color: 'var(--storefront-muted)' }}>
              {copy('booking.no_sessions')}
            </p>
          ) : (
            <div data-booking-tokens="only">
              <p className="mt-3 text-sm" style={{ color: 'var(--storefront-muted)' }}>
                {copy('booking.tokens_hint')}
              </p>
              {sessionCards(false)}
            </div>
          )
        ) : bookable.length === 0 && sessions.length === 0 ? (
          <p className="mt-3 text-sm" style={{ color: 'var(--storefront-muted)' }}>
            {copy('booking.none_free')}
          </p>
        ) : bookable.length === 0 ? (
          <div data-booking-tokens="beside">
            <p className="mt-3 text-sm" style={{ color: 'var(--storefront-muted)' }}>
              {copy('booking.tokens_hint')}
            </p>
            {sessionCards(true)}
          </div>
        ) : (
          <div
            className={cn(
              'mt-3 grid grid-cols-3 gap-2',
              !compact && 'sm:grid-cols-4 md:grid-cols-6',
            )}
          >
            {bookable.map((slot) => (
              <button
                key={slot.start_at}
                type="button"
                aria-pressed={slot.start_at === chosen}
                onClick={() => chooseSlot(slot.start_at)}
                className={cn(
                  'border px-2 py-2 text-sm',
                  slot.start_at === chosen && 'font-semibold',
                )}
                style={{
                  borderColor:
                    slot.start_at === chosen
                      ? 'var(--storefront-primary)'
                      : 'var(--storefront-surface)',
                  borderRadius: 'var(--storefront-radius)',
                }}
              >
                {formatTime(slot.start_at, locale, zone)}
                {/* Only where it means something: "1 left" under every time in
                    a one-chair salon is noise, and under the last place at a
                    three-chair one it is the reason to book now. */}
                {/* Not on a section that books by person: there a time is a
                    person, and "2 left" would count people, not places. */}
                {!byPerson && section.capacity_per_slot > 1 ? (
                  <span className="block text-xs" style={{ color: 'var(--storefront-muted)' }}>
                    {copy(slot.remaining === 1 ? 'booking.left_one' : 'booking.left_many', {
                      count: slot.remaining,
                    })}
                  </span>
                ) : null}
              </button>
            ))}
          </div>
        )}
        {!tokenMode && bookable.length > 0 && sessions.length > 0 ? (
          <div className="mt-6" data-booking-tokens="beside">
            <p className="text-sm font-semibold">{copy('booking.take_token')}</p>
            {sessionCards(true)}
          </div>
        ) : null}
      </fieldset>

      {/* Capped rather than left to the section's width. The strip and the slot
          grid above want every pixel of the page - they are scanned - but a
          name field two feet wide is a field nobody can tell is a name field.
          The same measure the shop's other forms are read at. */}
      <form
        className={cn('mt-6 grid max-w-3xl gap-3', !compact && 'sm:grid-cols-2')}
        onSubmit={submit}
        noValidate={preview}
      >
        <Field
          label={copy('section.field_name')}
          required
          value={values.name}
          onChange={set('name')}
        />
        <Field
          label={copy('account.phone')}
          type="tel"
          required
          value={values.phone}
          onChange={set('phone')}
        />
        <Field
          label={copy('checkout.field_email')}
          type="email"
          className="sm:col-span-2"
          value={values.email}
          onChange={set('email')}
        />
        {needsAddress ? (
          // A home visit with nowhere to visit is not a booking, so this one
          // is required - the server refuses it blank as well.
          <div className="sm:col-span-2">
            <Field
              label={copy('booking.address_field')}
              multiline
              required
              value={values.address}
              onChange={set('address')}
            />
          </div>
        ) : null}
        {offersWhatsApp ? (
          <WhatsAppOptIn
            className="sm:col-span-2"
            checked={whatsappOptIn}
            disabled={!values.phone.trim()}
            onChange={setWhatsAppOptIn}
          />
        ) : null}
        {section.ask_notes ? (
          <div className="sm:col-span-2">
            <Field
              label={section.notes_label}
              multiline
              value={values.notes}
              onChange={set('notes')}
            />
          </div>
        ) : null}
        {/* The merchant's own questions, after everything the form always
            asked - each a full row, as the notes are. */}
        {questions.map((question) => (
          <div key={question.id} className={compact ? undefined : 'sm:col-span-2'}>
            <QuestionField
              question={question}
              value={answers[question.id]}
              onChange={(value) =>
                setAnswers((current) => ({ ...current, [question.id]: value }))
              }
              variant="placeholder"
            />
          </div>
        ))}

        <BookingPaymentOptions payment={payment} className="sm:col-span-2" />

        <div className="sm:col-span-2">
          <button
            type="submit"
            // Disabled until a time is picked, because a booking without one is
            // not a thing the shopper meant to send - and the button saying so
            // is clearer than a form that refuses after they press it.
            disabled={state === 'sending' || paused !== null || (!chosen && !token && !preview)}
            className={cn('h-12 px-8 text-sm font-semibold disabled:opacity-60', compact && 'w-full')}
            style={STOREFRONT_BUTTON_STYLE}
          >
            {state === 'sending' ? copy('account.sending') : section.button_label}
          </button>
          <CheckoutPausedNote message={paused} />
          {chosen ? (
            <p className="mt-2 text-sm" style={{ color: 'var(--storefront-muted)' }}>
              {copy('booking.when_duration', {
                day: formatDay(chosen, locale, zone),
                time: formatTime(chosen, locale, zone),
                minutes: service.duration_minutes,
              })}
            </p>
          ) : token ? (
            <p className="mt-2 text-sm" style={{ color: 'var(--storefront-muted)' }} data-booking-token-pick>
              {copy('booking.your_token', { number: token.next_token })}
              {' · '}
              {copy('booking.when', {
                day: formatDay(token.estimated_at, locale, zone),
                time: copy('booking.approx', { time: formatTime(token.estimated_at, locale, zone) }),
              })}
            </p>
          ) : null}
        </div>
      </form>

      {state === 'error' ? (
        <p className="mt-3 text-sm" style={{ color: 'var(--storefront-muted)' }}>
          {error || copy('booking.error')}
        </p>
      ) : null}
    </div>
  );
}
